import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMerchant } from "../context/merchant";
import { AUTH_EXPIRED_EVENT } from "../api/axios/instance";

// Шинэ захиалга ирэхэд тоглодог дуу — modals/new-order.js-тэй ижил файл, ижил арга
const ALERT_SOUND_SRC = "/assets/sound/ringtone.mp3";

// Autoplay хаагдсан үед дууг дахин оролдох хэрэглэгчийн үйлдлүүд
// (touchend/click/keydown нь хөтөчид "user activation" болдог; pointerdown биш)
const GESTURE_EVENTS = ["click", "touchend", "keydown"];

/**
 * Нэвтрэлт дууссан үеийн бүтэн дэлгэцийн хаалт (App.js-д ГАНЦ удаа mount).
 *
 * api/axios/instance.js нь токен хүчингүй (401 / INVALID_TOKEN) хариу авахад
 * "e24:auth-expired" event цацна. Үгүй бол tablet чимээгүй "хөлдөж", төлөгдсөн
 * шинэ захиалгыг merchant анзаарахгүй өнгөрдөг байв. Товч дартал дуут дохио
 * давтагдана.
 */
export default function AuthExpiredOverlay() {
  const navigate = useNavigate();
  const { clearMerchantInfo, setNewOrderModalOpen } = useMerchant();
  const [show, setShow] = useState(false);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleExpired = () => setShow(true);
    window.addEventListener(AUTH_EXPIRED_EVENT, handleExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handleExpired);
  }, []);

  // Дуут дохио — overlay харагдаж байх хугацаанд тасралтгүй давтагдана
  useEffect(() => {
    if (!show) return undefined;

    let cancelled = false;
    const audio = new Audio(ALERT_SOUND_SRC);
    audio.loop = true;

    // Хөтөч autoplay-г хаасан бол (хуудас дөнгөж ачаалагдаж, хэрэглэгч хараахан
    // хүрээгүй) дэлгэцэнд хүрмэгц дахин оролдоно. "Дахин нэвтрэх" товчийг
    // алгасна — тэр нь дууг зогсооно.
    function handleGesture(e) {
      if (cancelled) return;
      if (buttonRef.current && buttonRef.current.contains(e.target)) return;
      Promise.resolve(audio.play())
        .then(removeGestureListeners)
        .catch(() => {});
    }
    function removeGestureListeners() {
      GESTURE_EVENTS.forEach((type) =>
        document.removeEventListener(type, handleGesture, true),
      );
    }

    Promise.resolve(audio.play()).catch((e) => {
      if (cancelled) return;
      console.log("Audio play error:", e);
      GESTURE_EVENTS.forEach((type) =>
        document.addEventListener(type, handleGesture, true),
      );
    });

    return () => {
      cancelled = true;
      removeGestureListeners();
      audio.pause();
      audio.currentTime = 0;
    };
  }, [show]);

  const handleRelogin = () => {
    setShow(false);
    // Токен + merchant state-ийг цэвэрлэж (захиалгын polling зогсоно) нэвтрэх
    // хуудас руу. Нэвтрэх хуудас нь "/" (App.js).
    // "Шинэ захиалга" цонх (<Routes>-оос гадна) нээлттэй байсан бол хаана —
    // эс бөгөөс нэвтрэх хуудсан дээр ringtone нь тасралтгүй дуугарсаар үлдэнэ.
    setNewOrderModalOpen(false);
    clearMerchantInfo();
    navigate("/", { replace: true });
  };

  if (!show) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="auth-expired-title"
      aria-describedby="auth-expired-desc"
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        // react-bootstrap modal (1055), .fab (99999)-оос дээр — юу ч нээлттэй байсан хаана
        zIndex: 100000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        backgroundColor: "rgba(0, 0, 0, 0.8)",
      }}
    >
      <div
        className="card border-0 shadow-lg text-center mb-0"
        style={{ width: "100%", maxWidth: 520 }}
      >
        <div className="card-body p-4">
          <div
            className="bg-danger rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
            style={{ width: 96, height: 96 }}
          >
            <i
              className="mdi mdi-lock-clock text-white"
              style={{ fontSize: "3.5rem", lineHeight: 1 }}
            />
          </div>
          <h3 id="auth-expired-title" className="mt-0 mb-2 text-danger">
            Нэвтрэлтийн хугацаа дууссан байна.
          </h3>
          <p id="auth-expired-desc" className="fs-4 mb-4">
            Шинэ захиалга хүлээн авахын тулд дахин нэвтэрнэ үү.
          </p>
          <button
            ref={buttonRef}
            type="button"
            className="btn btn-danger btn-lg w-100"
            onClick={handleRelogin}
          >
            <i className="mdi mdi-login me-1" /> Дахин нэвтрэх
          </button>
        </div>
      </div>
    </div>
  );
}
